import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { getMarket, triggerDecomposition } from '../../utils/api';
import { POLLING_CONFIG } from '../../config';
import './GraphBuilding.css';

const GraphBuilding = () => {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [market, setMarket] = useState(null);
  const [error, setError] = useState(null);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [logs, setLogs] = useState([]);

  // Trigger decomposition and start polling
  useEffect(() => {
    if (!marketId || hasTriggered) return;

    const startProcessing = async () => {
      try {
        // First fetch the market to check its status
        const response = await getMarket(marketId);
        const currentMarket = response.market;
        setMarket(currentMarket);
        setLogs(currentMarket.logs || []);

        // Only trigger decomposition if market is in 'created' state
        if (currentMarket.status === 'created') {
          try {
            await triggerDecomposition(marketId);
          } catch (triggerErr) {
            console.log('Trigger skipped (may already be processing):', triggerErr.message);
          }
        }

        setHasTriggered(true);
      } catch (err) {
        console.error('Error starting processing:', err);
        setError(err.message);
      }
    };

    startProcessing();
  }, [marketId, hasTriggered]);

  // Poll for updates
  useEffect(() => {
    if (!hasTriggered || !marketId) return;

    const terminalStatuses = ['complete', 'error'];

    const poll = async () => {
      try {
        const response = await getMarket(marketId);
        setMarket(response.market);
        setLogs(response.market.logs || []);

        // Continue polling if not in terminal state
        if (!terminalStatuses.includes(response.market.status)) {
          return true;
        }
        return false;
      } catch (err) {
        console.error('Polling error:', err);
        return false;
      }
    };

    let attempts = 0;
    const maxAttempts = POLLING_CONFIG.maxAttempts;

    const pollInterval = setInterval(async () => {
      attempts++;
      const shouldContinue = await poll();

      if (!shouldContinue || attempts >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, POLLING_CONFIG.interval);

    // Initial poll
    poll();

    return () => clearInterval(pollInterval);
  }, [hasTriggered, marketId]);

  const handleViewGraph = () => {
    navigate(`/graph/${marketId}`);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'created':
        return 'Initializing...';
      case 'decomposing':
        return 'Extracting Factors...';
      case 'identifying_intermediates':
        return 'Identifying Intermediates...';
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  const getStepIcon = (step) => {
    switch (step) {
      case 'init':
        return '>';
      case 'decompose':
        return '|';
      case 'intermediates':
        return '+';
      case 'complete':
        return '*';
      case 'error':
        return '!';
      default:
        return '-';
    }
  };

  if (error) {
    return (
      <div className="graph-building">
        <header className="graph-building__header">
          <div className="graph-building__header-content">
            <img src="/logo.png" alt="Composer" className="graph-building__logo" />
            <div className="graph-building__header-text">
              <h1 className="graph-building__title">Composer</h1>
              <p className="graph-building__subtitle">Factor Graph Builder</p>
            </div>
          </div>
        </header>
        <main className="graph-building__main">
          <Card title="Error">
            <p className="graph-building__error-text">{error}</p>
            <Button onClick={() => navigate('/')}>Back to Home</Button>
          </Card>
        </main>
      </div>
    );
  }

  const isComplete = market?.status === 'complete';
  const isError = market?.status === 'error';

  return (
    <div className="graph-building">
      <header className="graph-building__header">
        <div className="graph-building__header-content">
          <img src="/logo.png" alt="Composer" className="graph-building__logo" />
          <div className="graph-building__header-text">
            <h1 className="graph-building__title">Composer</h1>
            <p className="graph-building__subtitle">Factor Graph Builder</p>
          </div>
        </div>
      </header>

      <main className="graph-building__main">
        <Card>
          <div className="graph-building__question">
            <span className="graph-building__question-label">Market:</span>
            <span className="graph-building__question-text">
              {market?.question || 'Loading...'}
            </span>
          </div>

          <div className="graph-building__status">
            <span className={`graph-building__status-badge graph-building__status-badge--${market?.status || 'created'}`}>
              {getStatusLabel(market?.status)}
            </span>
          </div>

          <div className="graph-building__logs">
            <div className="graph-building__logs-header">
              <span className="graph-building__logs-title">Processing Log</span>
            </div>
            <div className="graph-building__logs-content">
              {logs.length === 0 ? (
                <div className="graph-building__log-entry">
                  <span className="graph-building__log-icon">...</span>
                  <span className="graph-building__log-message">Waiting for updates...</span>
                </div>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className="graph-building__log-entry">
                    <span className="graph-building__log-icon">{getStepIcon(log.step)}</span>
                    <span className="graph-building__log-step">[{log.step}]</span>
                    <span className="graph-building__log-message">{log.message}</span>
                  </div>
                ))
              )}
              {!isComplete && !isError && (
                <div className="graph-building__log-entry graph-building__log-entry--active">
                  <span className="graph-building__log-cursor">_</span>
                </div>
              )}
            </div>
          </div>

          {isComplete && (
            <div className="graph-building__complete">
              <div className="graph-building__complete-message">
                <span className="graph-building__complete-icon">*</span>
                Factor graph created successfully
              </div>
              <div className="graph-building__stats">
                <div className="graph-building__stat">
                  <span className="graph-building__stat-value">
                    {market.factors?.filter(f => f.type === 'atomic').length || 0}
                  </span>
                  <span className="graph-building__stat-label">Atomic Factors</span>
                </div>
                <div className="graph-building__stat">
                  <span className="graph-building__stat-value">
                    {market.factors?.filter(f => f.type === 'intermediate').length || 0}
                  </span>
                  <span className="graph-building__stat-label">Intermediates</span>
                </div>
                <div className="graph-building__stat">
                  <span className="graph-building__stat-value">
                    {market.edges?.length || 0}
                  </span>
                  <span className="graph-building__stat-label">Relationships</span>
                </div>
              </div>
              <Button onClick={handleViewGraph} fullWidth>
                View Factor Graph
              </Button>
            </div>
          )}

          {isError && (
            <div className="graph-building__error">
              <p>An error occurred during processing. Please try again.</p>
              <Button onClick={() => navigate('/')} variant="secondary">
                Back to Home
              </Button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default GraphBuilding;
